import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import Tooltip from 'components/common/Tooltip';
import Typography from '@material-ui/core/Typography';
import Link from 'components/common/Link';

import formatPrice from 'utils/formatPrice';

const styles = theme => ({
  row: {
    height: 20,
  },
  rowHead: {
    borderTop: `1px solid ${theme.palette.primary.A100}`,
    '&:first-child': {
      borderTop: 'none',
    },
    '& $cell': {
      paddingTop: 15,
      paddingBottom: 15,
      fontSize: 12,
      letterSpacing: '0.03em',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      color: theme.palette.grey[600],
      backgroundColor: theme.palette.primary.A200,
    },
  },
  rowBody: {
    '& $cell': {
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  cell: {
    padding: '0 8px 0 8px',
    border: 'none',
  },
  cellStatus: {
    textTransform: 'capitalize',
    fontWeight: 'bold',
  },
  cellStatusSent: {
    color: theme.palette.notification['500'],
  },
  cellStatusSuccess: {
    color: theme.palette.add['500'],
  },
  cellStatusError: {
    color: theme.palette.validation['500'],
  },
  tooltipGroupCode: {
    marginRight: 4,
  },
  tooltipAdjustmentCode: {
    color: theme.palette.primary[500],
  },
  tooltipGroupCodeContent: {
    color: theme.palette.primary[500],
  },
  adjustmentAmount: {
    marginRight: 4,
  },
  recoupment: {
    color: theme.palette.validation['500'],
  },
  rebill: {
    color: theme.palette.add['500'],
  },
  adjustmentGroupItem: {
    '& + $adjustmentGroupItem': {
      marginLeft: 4,
    },
  },
  adjustmentItem: {
    '& + $adjustmentItem': {
      marginLeft: 4,
    },
  },
});

const ClaimsTableRowItem = ({ Status, error, classes, showHead, ...otherProps }) => {
  const currentStatus = Status && Status.length > 0 ? Status[Status.length - 1] : null;
  const eraClaimLine = otherProps['ERA Claim Line'] && otherProps['ERA Claim Line'].length > 0
    ? otherProps['ERA Claim Line'][0]
    : null;
  return (
    <Fragment>
      {showHead &&
        <TableRow className={classNames(classes.row, classes.rowHead)}>   
          <TableCell className={classes.cell}>
            Line Status
          </TableCell>
          <TableCell className={classes.cell}>
            Date of Service
          </TableCell>
          <TableCell className={classes.cell}>
            HCPCS Code
          </TableCell>
          <TableCell className={classes.cell}>
            Modifiers
          </TableCell>
          <TableCell className={classes.cell}>
            Units
          </TableCell>
          <TableCell className={classes.cell}>
            Charge
          </TableCell>
          <TableCell className={classes.cell}>
            Units Paid
          </TableCell>
          <TableCell className={classes.cell}>
            Amount Paid
          </TableCell>
          <TableCell className={classes.cell}>
            Adjustment
          </TableCell>
          {(otherProps['Claim Line Status']
            && otherProps['Claim Line Status'][0]
            && otherProps['Claim Line Status'][0]['Claim Status Group']
            && otherProps['Claim Line Status'][0]['Claim Status Group'][0]) && (
              <Fragment>
                <TableCell className={classes.cell}>
                  Category
                </TableCell>
                <TableCell className={classes.cell}>
                  Code
                </TableCell>
                <TableCell className={classes.cell}>
                  Description
                </TableCell>
              </Fragment>
            )}
        </TableRow>
      }
      <TableRow className={classNames(classes.row, classes.rowBody)}>
        <TableCell
          className={classNames(classes.cell, classes.cellStatus, {
            [classes.cellStatusSent]: currentStatus.Category === 'Sent',
            [classes.cellStatusSuccess]: currentStatus.Category === 'Success',
            [classes.cellStatusError]: currentStatus.Category === 'Error',
          })}
        >
          {currentStatus ? currentStatus.Status : ''}
        </TableCell>
        <TableCell className={classes.cell}>
          <Link
            href={`/workflow/order/edit/${otherProps['Order ID']}`}
            inlineLabel
            breakWord
            target="_blank"
          >
            {otherProps['Date of Service From']}
            -
            {otherProps['Date of Service To']}
          </Link>
        </TableCell>
        <TableCell className={classes.cell}>
          {otherProps['Hcpc Code']}
        </TableCell>
        <TableCell className={classes.cell}>
          {(otherProps['Order Line'].Modifiers && otherProps['Order Line'].Modifiers.length > 0)
            && otherProps['Order Line'].Modifiers.match(/.{1,2}/g).join('-')}
        </TableCell>
        <TableCell className={classes.cell}>
          {otherProps.Units}
        </TableCell>
        <TableCell className={classes.cell}>
          {otherProps.Charge ? formatPrice(otherProps.Charge) : ''}
        </TableCell>
        <TableCell className={classes.cell}>
          {otherProps['ERA Claim Line'] && otherProps['ERA Claim Line'].length > 0
            ? otherProps['ERA Claim Line'].map((ERAClaimLineItem, index) => (
              <span
                className={classNames({
                  [classes.recoupment]: ERAClaimLineItem['Paid Units'] < 0,
                  [classes.rebill]: index !== 0 && ERAClaimLineItem.Paid >= 0,
                })}
                key={ERAClaimLineItem.ID}
              >
                {(index !== 0 && ERAClaimLineItem['Paid Units'] >= 0) ? '+' : ''}
                {ERAClaimLineItem['Paid Units']}
              </span>
            ))
            : ''}
          {otherProps['ERA Claim Line'] && otherProps['ERA Claim Line'].length > 1
            ? ` = ${otherProps['ERA Claim Line'].reduce((accumulator, ERAClaimLineItem) =>
                accumulator + (ERAClaimLineItem['Paid Units'] || 0), 0)}`
            : ''}
        </TableCell>
        <TableCell className={classes.cell}>
          {otherProps['ERA Claim Line'] && otherProps['ERA Claim Line'].length > 0
            ? otherProps['ERA Claim Line'].map((ERAClaimLineItem, index) => (
              <span
                className={classNames({
                  [classes.recoupment]: ERAClaimLineItem.Paid < 0,
                  [classes.rebill]: index !== 0 && ERAClaimLineItem.Paid >= 0,
                })}
                key={ERAClaimLineItem.ID}
              >
                {(index !== 0 && ERAClaimLineItem.Paid >= 0) ? '+' : ''}
                {formatPrice(ERAClaimLineItem.Paid)}
              </span>
            ))
            : ''}
          {otherProps['ERA Claim Line'] && otherProps['ERA Claim Line'].length > 1
            ? ` = ${formatPrice(otherProps['ERA Claim Line'].reduce((accumulator, ERAClaimLineItem) =>
                accumulator + (ERAClaimLineItem.Paid || 0), 0))}`
            : ''}
        </TableCell>
        <TableCell className={classes.cell}>
          {otherProps['ERA Claim Line'] && otherProps['ERA Claim Line'].length > 0
            ? otherProps['ERA Claim Line'].map((ERAClaimLineItem, index) => (
              <span
                className={classes.adjustmentGroupItem}
                key={ERAClaimLineItem.ID}
              >
                {ERAClaimLineItem.Adjustment.map(item => (
                  <span
                    className={classes.adjustmentItem}
                    key={item.ID}
                  >
                    <span
                      className={classes.adjustmentAmount}
                    >
                      {formatPrice(item.Amount)}
                    </span>
                    {item.Group && item.Group.Code &&
                      <Tooltip
                        className={classes.tooltipGroupCode}
                        content={<Typography>{item.Group.Description}</Typography>}
                        placement="top"
                      >
                        <span className={classes.tooltipGroupCodeContent}>{item.Group.Code}</span>
                        {` - `}
                      </Tooltip>
                    }
                    <Tooltip
                      className={classes.tooltipAdjustmentCode}
                      content={<Typography>{item.Code.Description}</Typography>}
                      placement="top"
                    >
                      {item.Code.Code}
                    </Tooltip>
                  </span>
                ))}
              </span>
            ))
            : ''}
        </TableCell>
        {(otherProps['Claim Line Status']
          && otherProps['Claim Line Status'][0]
          && otherProps['Claim Line Status'][0]['Claim Status Group']
          && otherProps['Claim Line Status'][0]['Claim Status Group'][0]) && (
            <Fragment>
              <TableCell className={classes.cell}>
                {otherProps['Claim Line Status'][0]['Claim Status Group'][0]['Claim Status Category'].Code}
              </TableCell>
              <TableCell className={classes.cell}>
                {otherProps['Claim Line Status'][0]['Claim Status Group'][0]['Claim Status'].Code}
              </TableCell>
              <TableCell className={classes.cell}>
                {otherProps['Claim Line Status'][0]['Claim Status Group'][0]['Claim Status'].Description}
              </TableCell>
            </Fragment>
          )}
      </TableRow>
    </Fragment>
  );
};

ClaimsTableRowItem.propTypes = {
  error: PropTypes.bool,
  classes: PropTypes.shape({
    row: PropTypes.string.isRequired,
    cell: PropTypes.string.isRequired,
  }).isRequired,
};

ClaimsTableRowItem.defaultProps = {
  error: false,
};

export default withStyles(styles)(ClaimsTableRowItem);



// WEBPACK FOOTER //
// ./src/components/reports/ClaimsTable/ClaimsTableRowItem.js